import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Environment, openDialogWrapper$ } from '@app/v2/shared/utils';
import { AuthorizationParams } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { ControlPageFacade, LoginError, LoginErrorCode } from '@ra-state';
import { ActionButtonStyles, DialogService, IDialogConfig, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { LayoutModule } from '@rockwell-automation-inc/layout';
import { ConfigService, CoreConfiguration } from '@rockwell-automation-inc/service';
import { LoggerService } from '@servicesV2/logger.service';
import { delay, map, tap, withLatestFrom } from 'rxjs';
import { AuthenticationService } from '../auth.service';

@Component({
  selector: 'app-landing',
  styleUrls: ['./landing.component.scss'],
  templateUrl: './landing.component.html',
  imports: [LayoutModule, AsyncPipe, NgTemplateOutlet],
})
export class LandingComponent {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly dialogSvc = inject(DialogService);
  private readonly config = inject<ConfigService<CoreConfiguration>>(ConfigService);
  private readonly logger = inject(LoggerService);

  returnTo: string;
  landingImageURL: string = `${environment.appConfiguration.csPortalUrl}/assets/images/LeMans-Cloud-Services.jpg`;

  @ViewChild('loginErrorTemplate') private readonly loginErrorTemplateRef: TemplateRef<any>;
  ENV = Environment.getInstance(environment.appConfiguration.csPortalUrl).env;
  isProdOrDemo = this.ENV === 'production' || this.ENV === 'demo';
  LoginErrorCode: string = LoginErrorCode.Unknown;
  openDialogWrapper$ = openDialogWrapper$;

  isAuthenticated$ = this.authenticationService.isAuthenticated$.pipe(
    withLatestFrom(this.route.queryParams),
    tap(([isAuthenticated, params]) => {
      this.returnTo = this.getReturnTo(params);
      if (isAuthenticated) {
        this.router.navigateByUrl(this.returnTo);
      }
    }),
    map(([isAuthenticated, _params]) => isAuthenticated),
  );

  getPreApprovalDialogConfig(email: string): IDialogConfig {
    return {
      title: 'Your email does not have access',
      message: `Your email ${email} is not approved to access the Hub in the ${this.ENV} environment. Please reach out to your engineering lead to get the email added to the approval list.`,
      messageType: NotificationType.Warning,
      buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Outlined }],
    };
  }

  getDemoUserDisabledDialogConfig(email: string): IDialogConfig {
    return {
      title: 'Unable to sign you in',
      message: `Your demo account ${email} is no longer active.`,
      messageType: NotificationType.Error,
      buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Outlined }],
    };
  }

  getDemoReservationDialogConfig(loginError: LoginError): IDialogConfig {
    return {
      title: 'Unable to sign you in',
      message: loginError.errorDescription,
      messageType: NotificationType.Error,
      buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Outlined }],
    };
  }

  getMissingProfileOrDefaultErrorConfig(): IDialogConfig {
    return {
      title: 'Unable to sign you in',
      contentProjection: { content: this.loginErrorTemplateRef }, //  TemplateRef content will be changed based on the LoginErrorCode
      message: '',
      messageType: NotificationType.Warning,
      buttons: [
        { label: 'Create ticket', buttonStyle: ActionButtonStyles.Main },
        { label: 'Close', buttonStyle: ActionButtonStyles.Outlined },
      ],
    };
  }

  openCiamSupportLink = (): void => {
    window.open(window['CIAM_SUPPORT_LINK'], '_blank');
  };

  getLoginErrorDialogConfig(loginError: LoginError): IDialogConfig {
    switch (loginError.errorCode) {
      case LoginErrorCode.PreApprovalCheckFailed:
        return this.getPreApprovalDialogConfig(loginError.email);
      case LoginErrorCode.DemoUserDisabled:
        return this.getDemoUserDisabledDialogConfig(loginError.email);
      case LoginErrorCode.ReservationHasExpired:
      case LoginErrorCode.ReservationNotFound:
        return this.getDemoReservationDialogConfig(loginError);
      case LoginErrorCode.MissingIdentity:
      case LoginErrorCode.MissingConnectionClaim:
      case LoginErrorCode.MissingUniqueIdentifierClaim:
      case LoginErrorCode.RootClaimMissing:
      case LoginErrorCode.InvalidConnectionClaim:
      case LoginErrorCode.InvalidMergeCandidates:
      case LoginErrorCode.MissingFamilyNameClaim:
      case LoginErrorCode.MissingGivenNameClaim:
      case LoginErrorCode.MissingEmailClaim:
        return this.getMissingProfileOrDefaultErrorConfig();
      case LoginErrorCode.Unknown:
      default:
        this.LoginErrorCode = LoginErrorCode.Unknown;
        return this.getMissingProfileOrDefaultErrorConfig();
    }
  }

  private handleLoginError(loginError: LoginError): void {
    this.LoginErrorCode = loginError.errorCode;
    // Opening Dialog from component because of contentProjection config,
    // NgRx state management doesn't allow to render the dynamic UI generation from effects.
    this.openDialogWrapper$(this.dialogSvc, this.getLoginErrorDialogConfig(loginError)).subscribe((result) => {
      if (result && result.buttonStyle === ActionButtonStyles.Main) {
        this.openCiamSupportLink();
      } else if (result && result.buttonStyle === ActionButtonStyles.Outlined) {
        this.controlPageFacade.logoutSession();
      }
    });
  }

  loginError$ = this.authenticationService.error$.pipe(
    delay(100), // Delay to ensure the render of loginErrorTemplateRef
    tap((errorContext: any) => {
      try {
        const loginError: LoginError = JSON.parse(errorContext.error_description);
        if (loginError) {
          this.handleLoginError(loginError);
        }
      } catch (exp) {
        this.logger.error('error parsing login error context - ', exp, errorContext);
        const loginError: LoginError = {
          errorCode: LoginErrorCode.Unknown,
          errorDescription: LoginErrorCode.Unknown,
          email: LoginErrorCode.Unknown,
        };
        this.handleLoginError(loginError);
      }
    }),
  );

  getReturnTo(params: Params): string {
    const searchParams = new URLSearchParams();
    for (const key of Object.keys(params)) {
      if (key !== 'returnTo') {
        searchParams.set(key, params[key]);
      }
    }
    const searchParamsStr = searchParams.toString();
    let returnTo = `${params.returnTo}`;
    if (searchParamsStr) {
      returnTo = returnTo.concat(`?${searchParams.toString()}`);
    }
    return returnTo;
  }
  private readonly loginLinkKey = 'linklogin';
  private readonly demoLinkConnection = 'DemoLink';
  private extractLoginHintFromUrl(): string | undefined {
    const resid = this.route.snapshot.paramMap.get('resid');
    const slug = this.route.snapshot.paramMap.get('slug');
    if (resid && slug) {
      return `${resid}/${slug}`;
    } else {
      return undefined;
    }
  }

  login(): void {
    const loginHint = this.extractLoginHintFromUrl();
    const connection = loginHint ? this.demoLinkConnection : this.config.config?.authConfig?.connection;
    if (connection) {
      const authParams: AuthorizationParams = {
        connection: connection,
      };
      if (loginHint) {
        authParams.login_hint = loginHint;
      }
      this.authenticationService.login(authParams, this.returnTo);
    } else {
      this.authenticationService.login(undefined, this.returnTo);
    }
  }
}
